import { debounce } from 'lodash';
import { useMemo, useState, useEffect } from 'react';

import {
  Box,
  Chip,
  Paper,
  Stack,
  Button,
  Select,
  Divider,
  MenuItem,
  TextField,
  IconButton,
  InputLabel,
  Typography,
  FormControl,
  Autocomplete,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { Skill } from 'src/services/jobs/jobs.types';
import { EntityLabel, EntityLabelType } from 'src/services/utils/utils.types';
import { useGetSkillsQuery, useSearchLabelsQuery } from 'src/services/utils/utils.service';
import { BoolNode, SearchAtom, BoolOperator, SearchOperator } from 'src/services/common.types';

import Iconify from 'src/components/iconify';

import { FieldOption } from '../types/search.types';
import { getFieldOptions } from '../utils/search.utils';

interface AdvancedSearchBuilderProps {
  value: BoolNode;
  onChange: (value: BoolNode) => void;
}

// Component to render a single search atom
function SearchAtomEditor({
  atom,
  onChange,
  onRemove,
  fieldOptions
}: {
  atom: SearchAtom;
  onChange: (atom: SearchAtom) => void;
  onRemove: () => void;
  fieldOptions: FieldOption[];
}) {
  const { t } = useTranslate();
  const fieldOption = fieldOptions.find((option) => option.id === atom.field);

  const [skillsSearchQuery, setSkillsSearchQuery] = useState('');
  const [labelsSearchQuery, setLabelsSearchQuery] = useState('');

  const { data: skillsData } = useGetSkillsQuery({
    page: 1,
    per_page: 100,
    name: skillsSearchQuery
  });

  const { currentData: availableLabels } = useSearchLabelsQuery({
    label: labelsSearchQuery ?? undefined,
    type: EntityLabelType.CANDIDATE,
    page: 1,
    per_page: 10
  });

  const handleFieldChange = (field: string) => {
    const newFieldOption = fieldOptions.find((option) => option.id === field);
    onChange({
      ...atom,
      field,
      operator: newFieldOption?.defaultOperator || 'eq',
      value: undefined,
    });
  };

  const handleOperatorChange = (operator: SearchOperator) => {
    onChange({
      ...atom,
      operator,
    });
  };

  const handleValueChange = (value: any) => {
    onChange({
      ...atom,
      value,
    });
  };

  const handleVariantChange = (variant: string) => {
    onChange({
      ...atom,
      variant,
    });
  };

  const onSkillsInputChange = useMemo(
    () => debounce((value: string) => {
      setSkillsSearchQuery(value);
    }, 500),
    [setSkillsSearchQuery]
  );

  const onLabelsInputChange = useMemo(
    () => debounce((value: string) => {
      setLabelsSearchQuery(value);
    }, 500),
    [setLabelsSearchQuery]
  );

  const renderValueInput = () => {
    if (!fieldOption) return null;

    switch (fieldOption.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            value={atom.value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={fieldOption.placeholder}
            size="small"
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            value={atom.value || ''}
            onChange={(e) => handleValueChange(Number(e.target.value))}
            placeholder={fieldOption.placeholder}
            size="small"
            InputProps={{ inputProps: { min: 0 } }}
          />
        );
      case 'select':
        return (
          <FormControl fullWidth size="small">
            <Select
              value={atom.value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>{t('common.none')}</em>
              </MenuItem>
              {fieldOption.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'multiselect':
        if (fieldOption.id === 'skills' && skillsData) {
          return (
            <Autocomplete
              multiple
              size="small"
              freeSolo
              options={skillsData.results.map((skill) => skill.name)}
              value={atom.value || []}
              onChange={(_, newValue) => handleValueChange(newValue)}
              onInputChange={(_e, value) => onSkillsInputChange(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={fieldOption.placeholder}
                />
              )}
            />
          );
        } if (fieldOption.id === 'labels' && availableLabels) {
          return (
            <Autocomplete
              multiple
              size="small"
              options={availableLabels.results.map((label) => label.label)}
              value={atom.value || []}
              onChange={(_, newValue) => handleValueChange(newValue)}
              onInputChange={(_e, value) => onLabelsInputChange(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={availableLabels.results.find((l) => l.id === option)?.label || option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={fieldOption.placeholder}
                />
              )}
            />
          );
        }
        return (
          <Autocomplete
            multiple
            size="small"
            freeSolo
            options={fieldOption.options?.map((option) => option.value) || []}
            value={atom.value || []}
            onChange={(_, newValue) => handleValueChange(newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={fieldOption.options?.find((o) => o.value === option)?.label || option}
                  size="small"
                />
              ))
            }
            renderOption={(props, option: string, { selected }) => {
              const optionLabel = fieldOption.options?.find((o) => o.value === option)?.label || option;
              return (
                <li {...props}>
                  <Typography variant="body2">{optionLabel}</Typography>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={fieldOption.placeholder}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Field</InputLabel>
          <Select
            value={atom.field}
            onChange={(e) => handleFieldChange(e.target.value)}
            label="Field"
          >
            {fieldOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {
          fieldOption && fieldOption.variant && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{fieldOption.variant.label}</InputLabel>
              <Select
                value={atom.variant || fieldOption.variant.options[0].value}
                onChange={(e) => handleVariantChange(e.target.value)}
                label="Period"
              >
                {fieldOption.variant.options.map((variantOption) => (
                  <MenuItem key={variantOption.value} value={variantOption.value}>
                    {variantOption.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        }

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t(`components.advanced_search.operator`)}</InputLabel>
          <Select
            value={atom.operator}
            onChange={(e) => handleOperatorChange(e.target.value as SearchOperator)}
            label="Operator"
          >
            {fieldOption?.operators ? (
              fieldOption.operators.map((op) => (
                <MenuItem key={op} value={op}>
                  {t(`components.advanced_search.filter_operators.${op}`)}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={fieldOption?.defaultOperator || 'eq'}>
                {t(`components.advanced_search.filter_operators.${fieldOption?.defaultOperator || 'eq'}`)}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }}>{renderValueInput()}</Box>

        <IconButton onClick={onRemove} color="error">
          <Iconify icon="eva:trash-2-outline" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

// Component to render a boolean node (AND, OR, NOT)
function BoolNodeEditor({
  node,
  onChange,
  onRemove,
  level = 0,
  fieldOptions,
  skills,
  labels,
}: {
  node: BoolNode;
  onChange: (node: BoolNode) => void;
  onRemove: () => void;
  level?: number;
  fieldOptions: FieldOption[];
  skills?: Skill[];
  labels?: EntityLabel[];
}) {
  const { t } = useTranslate();

  const handleOperatorChange = (op: BoolOperator) => {
    onChange({
      ...node,
      op,
    });
  };

  const handleAddCondition = () => {
    const defaultField = fieldOptions[0].id;
    const defaultOperator = fieldOptions[0].defaultOperator || 'eq';

    onChange({
      ...node,
      children: [
        ...node.children,
        {
          field: defaultField,
          operator: defaultOperator,
          value: '',
        } as SearchAtom,
      ],
    });
  };

  const handleAddGroup = () => {
    onChange({
      ...node,
      children: [
        ...node.children,
        {
          op: 'AND',
          children: [],
        } as BoolNode,
      ],
    });
  };

  const handleChildChange = (index: number, child: BoolNode | SearchAtom) => {
    const newChildren = [...node.children];
    newChildren[index] = child;
    onChange({
      ...node,
      children: newChildren,
    });
  };

  const handleRemoveChild = (index: number) => {
    const newChildren = [...node.children];
    newChildren.splice(index, 1);
    onChange({
      ...node,
      children: newChildren,
    });
  };

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        ml: level * 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>{t('components.advanced_search.operator')}</InputLabel>
            <Select
              value={node.op}
              onChange={(e) => handleOperatorChange(e.target.value as BoolOperator)}
              label="Operator"
            >
              <MenuItem value="AND">{t('components.advanced_search.bool_operators.and')}</MenuItem>
              <MenuItem value="OR">{t('components.advanced_search.bool_operators.or')}</MenuItem>
              <MenuItem value="NOT">{t('components.advanced_search.bool_operators.not')}</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {
              node.op === 'AND' && t('candidates.search.all_conditions_must_match')
            }
            {
              node.op === 'OR' && t('candidates.search.any_condition_can_match')
            }
            {
              node.op === 'NOT' && t('candidates.search.none_of_conditions_should_match')
            }
          </Typography>

          {level > 0 && (
            <IconButton onClick={onRemove} color="error">
              <Iconify icon="eva:trash-2-outline" />
            </IconButton>
          )}
        </Stack>

        <Divider />

        {node.children.map((child, index) => (
          <Box key={index}>
            {'op' in child ? (
              <BoolNodeEditor
                node={child as BoolNode}
                onChange={(newNode) => handleChildChange(index, newNode)}
                onRemove={() => handleRemoveChild(index)}
                level={level + 1}
                fieldOptions={fieldOptions}
                skills={skills}
                labels={labels}
              />
            ) : (
              <SearchAtomEditor
                atom={child as SearchAtom}
                onChange={(newAtom) => handleChildChange(index, newAtom)}
                onRemove={() => handleRemoveChild(index)}
                fieldOptions={fieldOptions}
              />
            )}
          </Box>
        ))}

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleAddCondition}
          >
            {t('candidates.search.add_condition')}
          </Button>
          <Button
            size="small"
            startIcon={<Iconify icon="eva:folder-add-fill" />}
            onClick={handleAddGroup}
          >
            {t('candidates.search.add_group')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function AdvancedSearchBuilder({
  value,
  onChange
}: AdvancedSearchBuilderProps) {
  const { t } = useTranslate();
  const fieldOptions = getFieldOptions();

  // initialize with an empty AND node if value is empty
  useEffect(() => {
    if (!value || !value.op || !value.children) {
      onChange({
        op: 'AND',
        children: [],
      });
    }
  }, [value, onChange]);

  if (!value || !value.op || !value.children) {
    return null;
  }

  return (
    <Box>

      <Typography variant="body2" color="text.secondary" paragraph>
        {t('candidates.search.advanced_search_description')}
      </Typography>

      <BoolNodeEditor
        node={value}
        onChange={onChange}
        onRemove={() => { }}
        fieldOptions={fieldOptions}
      />
    </Box>
  );
}